// The visible date, rather than a separate confirmation flag, authorizes exit.
export const FUTURE_FOLLOW_UP_REQUIRED_MESSAGE =
  "Agende a próxima conversa antes de sair deste atendimento.";

export const NEXT_CONTACT_DATE_FIELDS = Object.freeze([
  "nextContactDate",
  "followupDate",
  "followUpDate",
  "followUp",
  "next_contact_date",
  "next_contact",
  "proximaConversaDate",
  "proximaConversa",
  "dataProximoContato",
  "data_proximo_contato",
  "proxima_data_contato",
  "scheduledFollowUpDate",
]);

// A phone/name-only fallback is not an open CRM record and must never own an
// exit lock. Conversation entities are persisted records with a canonical id.
export function hasRealFollowUpRecord(record) {
  return !!record
    && typeof record === "object"
    && !Array.isArray(record)
    && record.id !== null
    && record.id !== undefined
    && String(record.id).trim() !== "";
}

const COMMERCIAL_PROJECTION_META = "__commercialProjection";
const COMMERCIAL_PROJECTED_DATE_FIELDS = Object.freeze([
  "proxima_data_contato",
  "followUp",
  "nextContactDate",
]);

const SAO_PAULO_TIME_ZONE = "America/Sao_Paulo";

export function saoPauloDateKey(now = new Date()) {
  if (!(now instanceof Date) || Number.isNaN(now.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: SAO_PAULO_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const value = type => parts.find(part => part.type === type)?.value;
  return `${value("year")}-${value("month")}-${value("day")}`;
}

function isCalendarDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const [, year, month, day] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  return date.getUTCFullYear() === Number(year)
    && date.getUTCMonth() === Number(month) - 1
    && date.getUTCDate() === Number(day);
}

// Normalizes both HTML date inputs and persisted timestamps to the São Paulo
// calendar.  Date-only values intentionally stay date-only: they represent the
// calendar day selected by the consultant, not a UTC instant.
export function normalizeNextContactDate(value) {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (isCalendarDate(trimmed)) return trimmed;
    const timestamp = new Date(trimmed);
    return Number.isNaN(timestamp.getTime()) ? "" : saoPauloDateKey(timestamp);
  }
  if (value instanceof Date) return saoPauloDateKey(value);
  if (typeof value === "number") return saoPauloDateKey(new Date(value));
  return "";
}

export function persistedNextContactDate(record) {
  if (!record || typeof record !== "object") return "";
  for (const field of NEXT_CONTACT_DATE_FIELDS) {
    const date = normalizeNextContactDate(record[field]);
    if (date) return date;
  }
  return "";
}

export function persistedNextContactIsCompleted(record) {
  if (!record || typeof record !== "object") return false;
  if (record.followUpCompleted === true
    || record.followupCompleted === true
    || record.nextContactCompleted === true
    || Boolean(record.followUpDoneAt)
    || Boolean(record.followupDoneAt)) return true;
  const nextContactStatus = String(record.nextContactStatus || "")
    .trim()
    .toLocaleLowerCase("pt-BR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  return ["completed", "done", "concluido"].includes(nextContactStatus);
}

// This exception is intentionally narrow.  It recognizes only the canonical
// disqualification values used by the three conversation record types; a
// generic terminal, closed, or sold status must still have a current-or-future follow-up.
export function persistedRecordIsDisqualified(record) {
  if (!record || typeof record !== "object") return false;
  return record.desqualificado === true
    || record.estagio === "desqualificado"
    || record.stage === "semInteresse"
    || record.stage === "sem_interesse";
}

// This is the explicit commercial closing stage, not an unanswered-message
// indicator, attempt count, or a disqualification reason.
export function persistedRecordIsNoResponse(record) {
  return !!record && typeof record === "object" && record.stage === "sem_resposta";
}

// Indicação não precisa de uma próxima data para sair da conversa.
export function persistedRecordIsReferral(record) {
  if (!record || typeof record !== "object" || record instanceof Date) return false;
  const norm = value => String(value || "").trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const originalOrigin = ["origem", "source", "origem_canal", "origin", "origemCadastro"]
    .map(field => record[field])
    .find(value => String(value || "").trim() !== "");
  if (originalOrigin !== undefined) return norm(originalOrigin) === "indicacao";
  return Boolean(record.indicacaoId || record.referralAttribution);
}

export function confirmedNextContactSnapshot(recordOrDate) {
  if (recordOrDate && typeof recordOrDate === "object" && !(recordOrDate instanceof Date)
    && Object.prototype.hasOwnProperty.call(recordOrDate, "date")
    && Object.prototype.hasOwnProperty.call(recordOrDate, "completed")) {
    return Object.freeze({
      date:normalizeNextContactDate(recordOrDate.date),
      completed:recordOrDate.completed === true,
      disqualified:recordOrDate.disqualified === true || persistedRecordIsDisqualified(recordOrDate),
      noResponse:recordOrDate.noResponse === true || persistedRecordIsNoResponse(recordOrDate),
      referral:recordOrDate.referral === true || persistedRecordIsReferral(recordOrDate),
    });
  }
  return Object.freeze({
    date:typeof recordOrDate === "object" && !(recordOrDate instanceof Date)
      ? persistedNextContactDate(recordOrDate)
      : normalizeNextContactDate(recordOrDate),
    completed:typeof recordOrDate === "object" && !(recordOrDate instanceof Date)
      ? persistedNextContactIsCompleted(recordOrDate)
      : false,
    disqualified:typeof recordOrDate === "object" && !(recordOrDate instanceof Date)
      ? persistedRecordIsDisqualified(recordOrDate)
      : false,
    referral:persistedRecordIsReferral(recordOrDate),
    noResponse:persistedRecordIsNoResponse(recordOrDate),
  });
}

// Commercial projections are built only from the persisted tracking snapshot.
// Accept the projected date only while the record still carries the adapter's
// metadata and the visible value still matches that metadata. Local drafts and
// optimistic edits have neither guarantee and must continue through saved().
export function confirmedCommercialProjectionSnapshot(record) {
  if (!record || typeof record !== "object") return null;
  const fields = record[COMMERCIAL_PROJECTION_META]?.fields;
  if (!fields || typeof fields !== "object") return null;
  for (const field of COMMERCIAL_PROJECTED_DATE_FIELDS) {
    const descriptor = fields[field];
    if (!descriptor || !Object.prototype.hasOwnProperty.call(descriptor, "projectedValue")) continue;
    if (JSON.stringify(record[field]) !== JSON.stringify(descriptor.projectedValue)) continue;
    return confirmedNextContactSnapshot({
      date: descriptor.projectedValue,
      completed: false,
      disqualified: false,
      referral:persistedRecordIsReferral(record),
    });
  }
  return null;
}

export function hasValidFutureNextContactDate(recordOrDate, now = new Date()) {
  const snapshot = confirmedNextContactSnapshot(recordOrDate);
  if (snapshot.disqualified || snapshot.noResponse || snapshot.referral) return true;
  if (snapshot.completed) return false;
  const date = snapshot.date;
  const today = saoPauloDateKey(now);
  return Boolean(date && today && date >= today);
}

export function evaluateFutureFollowUpExit(recordOrDate, now = new Date()) {
  return {
    allowed: hasValidFutureNextContactDate(recordOrDate, now),
    message: FUTURE_FOLLOW_UP_REQUIRED_MESSAGE,
  };
}

export function runGuardedConversationAction(guard, action) {
  if (!guard?.canExit() || typeof action !== "function") return false;
  action();
  return true;
}

// Opening snapshots fence confirmed terminal stages. Draft getters determine
// date validity; async persistence failures always offer an explicit exit.
export function createActiveConversationGuard({ now = () => new Date() } = {}) {
  const active = new Map();
  const owners = new Map();
  let foregroundOwner = null;
  let sequence = 0;
  const currentOptions = entry => {
    const registrations = [...active.values()].filter(value => value.owner === entry?.owner);
    return registrations.reverse().find(value => value.options)?.options;
  };

  const evaluate = token => {
    const owner = token === undefined ? foregroundOwner : active.get(token)?.owner;
    const entry = owners.get(owner);
    const options = currentOptions({ owner });
    const visible = options?.getDate ? normalizeNextContactDate(options.getDate()) : entry?.confirmed.date;
    const exempt = entry?.confirmed.disqualified || entry?.confirmed.noResponse || entry?.confirmed.referral
      || options?.isExempt?.() === true;
    const valid = options?.getDate
      ? Boolean(visible && visible >= saoPauloDateKey(now()))
      : hasValidFutureNextContactDate(entry?.confirmed, now());
    if (entry && !options?.automaticExit && !exempt && !valid) {
      return { allowed: false, message: FUTURE_FOLLOW_UP_REQUIRED_MESSAGE, owner };
    }
    return { allowed: true, message: FUTURE_FOLLOW_UP_REQUIRED_MESSAGE, owner: null };
  };

  return Object.freeze({
    register(owner, persistedDate, options) {
      const token = `${++sequence}`;
      // Mounted duplicates share one confirmation. An old opening snapshot
      // must never overwrite a successful save from another token.
      if (!owners.has(owner)) owners.set(owner, { confirmed:confirmedNextContactSnapshot(persistedDate) });
      active.set(token, { owner, options });
      foregroundOwner = owner;
      return token;
    },
    updatePersistedDate(token, persistedDate, { saved = true } = {}) {
      const entry = active.get(token);
      if (!entry) return false;
      owners.get(entry.owner).confirmed = confirmedNextContactSnapshot(persistedDate);
      if (saved) owners.get(entry.owner).savedDate = normalizeNextContactDate(
        owners.get(entry.owner).confirmed.date,
      );
      return true;
    },
    unregister(token) {
      const entry = active.get(token);
      if (!entry) return false;
      active.delete(token);
      if (![...active.values()].some(other => other.owner === entry.owner)) {
        owners.delete(entry.owner);
        // Do not resurrect an older mounted/hidden conversation.
        if (foregroundOwner === entry.owner) foregroundOwner = null;
      }
      return true;
    },
    evaluate,
    canExit(token) {
      const result = evaluate(token);
      if (!result.allowed) {
        const options = currentOptions({ owner:result.owner });
        const value = options?.getDate?.();
        options?.report?.(!value ? "empty_date" : normalizeNextContactDate(value) ? "past_date" : "invalid_date");
      }
      return result.allowed;
    },
    requestAction(action, token) {
      const decision = evaluate(token);
      if (!decision.allowed) {
        this.canExit(token);
        if (typeof alert === "function") alert(FUTURE_FOLLOW_UP_REQUIRED_MESSAGE);
        return false;
      }
      const owner = token === undefined ? foregroundOwner : active.get(token)?.owner;
      const entry = owners.get(owner);
      const options = currentOptions({ owner });
      if (!entry || !options?.save || (!options?.automaticExit && (entry.confirmed.disqualified || entry.confirmed.noResponse || entry.confirmed.referral
        || options?.isExempt?.() === true
        || entry.savedDate === normalizeNextContactDate(options.getDate())))) {
        action(); return true;
      }
      if (entry.pending) return false;
      const date = normalizeNextContactDate(options.getDate());
      const context = { reminderId:`next-contact-${Date.now()}-${++sequence}` };
      let left = false;
      const leave = () => {
        if (left) return;
        left = true;
        if (owners.get(owner) !== entry || foregroundOwner !== owner) return;
        action();
      };
      const attempt = async () => {
        entry.pending = true;
        try {
          await options.save(date, context);
          entry.savedDate = date;
          leave();
          if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("arflow:next-contact-settled", {
            detail:{ operationId:context.reminderId },
          }));
        } catch (error) {
          options.report?.("save_failed");
          throw error;
        } finally { entry.pending = false; }
      };
      entry.pending = true;
      if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("arflow:next-contact-pending", {
        detail:{ leave, operationId:context.reminderId },
      }));
      Promise.resolve().then(attempt).catch(() => {
        if (left || owners.get(owner) !== entry || foregroundOwner !== owner) return;
        if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("arflow:next-contact-failed", {
          detail:{ retry:attempt, leave, operationId:context.reminderId },
        }));
      });
      return false;
    },
    activeCount() {
      return active.size;
    },
  });
}

// The application has one active workspace at a time.  Exporting its registry
// keeps independently-mounted modal/panel owners on the same exit decision.
export const activeConversationExitGuard = createActiveConversationGuard();

// Browser integration is opt-in so tests and server-side imports remain pure.
export function installBeforeUnloadGuard(guard, target = window) {
  const onBeforeUnload = event => {
    if (guard.canExit()) return;
    event.preventDefault();
    event.returnValue = "";
  };
  target.addEventListener("beforeunload", onBeforeUnload);
  return () => target.removeEventListener("beforeunload", onBeforeUnload);
}